const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL || "http://localhost:3376";

/**
 * @async
 * @description Generates a response to a Google review using the backend API.
 * The backend will use the review content, clinic information, and any specified additions or subtractions to create a tailored response.  
 * @param {String} reviewId The id of the Google review we're making a reply to.
 * @param {String} review The content of the review, can be empty.
 * @param {String} toBeAdded The things that should be included in the generated review.
 * @param {String} toBeSubtracted The things that should not be included in the generated review.
 * @param {String} responsesToAvoid The responses that should be avoided in similarity to the generated review.
 * @return {Object} An object containing the success status and the generated response or an error message.
 */
export async function generateGoogleReviewResponse(reviewId, review="", rating, toBeAdded = "", toBeSubtracted = "", responsesToAvoid = "") {
  if (!reviewId || typeof reviewId !== "string") {
    return { success: false, message: "Invalid review ID. Review ID must be a non-empty string." };
  } else if (typeof review !== "string") {
    return { success: false, message: "Invalid review content. Review content must be a string" };
  }

  try {
    const url = new URL("/api/generate-response", API_BASE_URL);

    const res = await fetch(url.toString(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        reviewId,
        reviewContent: review,
        reviewRating: rating,
        toBeAdded,
        toBeSubtracted,
        responsesToAvoid }),
    });

    const response = await res.json();
    if (response.success) {
      return { success: true, response: response.response };
    } else {
      return { success: false, message: response.error || "Failed to generate response." };
    }
  } catch (error) {
    console.error("Error generating review response:", error);
    return { success: false, message: "An error occurred while generating the response. Please try again." };
  }
}

export async function postGoogleReviewResponse(reviewId, responseContent) {
  if (!reviewId || typeof reviewId !== "string") {
    return { success: false, message: "Invalid review ID. Review ID must be a non-empty string." };
  } else if (!responseContent || typeof responseContent !== "string") {
    return { success: false, message: "Invalid response content. Response content must be a non-empty string." };
  }

  try {
    const url = new URL("/api/post-reply/google", API_BASE_URL);

    const res = await fetch(url.toString(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({ reviewId, replyContent:responseContent }),
    });

    const response = await res.json();
    if (response.success) {
      return { success: true, message: response.message || "Response posted successfully." };
    } else {
      return { success: false, message: response.error || "Failed to post response." };
    }
  } catch (error) {
    console.error("Error posting review response:", error);
    return { success: false, message: "An error occurred while posting the response. Please try again." };
  }
}